import { useId, type ReactNode } from "react";
import {
  alpha,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DialogShell } from "./DialogShell";

export type ConfirmTone = "primary" | "error" | "warning" | "info" | "success";

/** Fallbacks when no `icon` is passed. MUI icons for now; when the lucide
 *  migration reaches dialogs, this map is the single place to swap. */
const DEFAULT_TONE_ICONS: Record<ConfirmTone, ReactNode> = {
  primary: <HelpOutlineIcon />,
  error: <ErrorOutlineRoundedIcon />,
  warning: <WarningAmberRoundedIcon />,
  info: <InfoOutlinedIcon />,
  success: <CheckCircleOutlineIcon />,
};

export interface ConfirmDialogProps {
  open: boolean;
  /** Dismiss intent: Cancel button (unless `onCancel` is set), corner X,
   *  backdrop, Escape. Suppressed while `loading`; inert under `blocking`. */
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Body copy under the title. */
  description?: ReactNode;
  /** Extra content below the description: Alerts, summary lists, a TextField. */
  children?: ReactNode;
  /** Color of the hero circle, and of the confirm button unless `confirmColor` overrides.
   *  `error` is the destructive styling. Default `primary`. */
  tone?: ConfirmTone;
  /** Confirm-button color when it must differ from the hero (e.g. warning hero, error button). */
  confirmColor?: ConfirmTone;
  /** Hero icon. MUI/lucide icons inherit the circle's color via currentColor. */
  icon?: ReactNode;
  /** "solid": filled circle with white icon. "tinted": alpha-tinted circle with toned icon. */
  iconVariant?: "solid" | "tinted";
  confirmLabel?: string;
  cancelLabel?: string;
  /** Distinct cancel ACTION (e.g. "Save & Exit"). Defaults to `onClose`. */
  onCancel?: () => void;
  confirmStartIcon?: ReactNode;
  /** Shows a spinner on confirm and disables every dismissal affordance. */
  loading?: boolean;
  confirmDisabled?: boolean;
  /** Must-acknowledge mode: no Escape, no backdrop close, no corner X. */
  blocking?: boolean;
  /** Hides the cancel button (acknowledge-only dialogs). */
  singleAction?: boolean;
  /** Defaults to `!blocking`. */
  showCloseButton?: boolean;
  maxWidth?: 420 | 480;
  /** Default true — every existing confirmation autofocuses its confirm button. */
  confirmAutoFocus?: boolean;
}

/**
 * The app's single confirmation archetype: centered hero icon circle, title,
 * description, stacked full-width actions (contained confirm over text
 * cancel). Codifies the pattern previously copy-pasted across ~18 dialogs.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  tone = "primary",
  confirmColor,
  icon,
  iconVariant = "solid",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onCancel,
  confirmStartIcon,
  loading = false,
  confirmDisabled = false,
  blocking = false,
  singleAction = false,
  showCloseButton,
  maxWidth = 420,
  confirmAutoFocus = true,
}: ConfirmDialogProps) {
  const theme = useTheme();
  const titleId = useId();
  const descriptionId = useId();
  const buttonColor = confirmColor ?? tone;
  const toneMain = theme.palette[tone].main;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      closeDisabled={loading}
      blocking={blocking}
      showCloseButton={showCloseButton}
      maxWidth={maxWidth}
      titleId={titleId}
      descriptionId={description ? descriptionId : undefined}
    >
      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: iconVariant === "solid" ? toneMain : alpha(toneMain, 0.12),
            color: iconVariant === "solid" ? theme.palette.common.white : toneMain,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: `0 4px 14px ${alpha(toneMain, 0.3)}`,
            "& svg": { fontSize: 32 },
          }}
        >
          {icon ?? DEFAULT_TONE_ICONS[tone]}
        </Box>

        <Typography
          id={titleId}
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            id={descriptionId}
            variant="body1"
            component="div"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            {description}
          </Typography>
        )}

        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": { marginLeft: 0 },
        }}
      >
        <Button
          onClick={onConfirm}
          color={buttonColor}
          variant="contained"
          startIcon={confirmStartIcon}
          loading={loading}
          disabled={confirmDisabled}
          fullWidth
          disableElevation
          autoFocus={confirmAutoFocus}
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          {confirmLabel}
        </Button>
        {!singleAction && (
          <Button
            onClick={onCancel ?? onClose}
            disabled={loading}
            color="inherit"
            variant="text"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              color: "text.secondary",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            {cancelLabel}
          </Button>
        )}
      </DialogActions>
    </DialogShell>
  );
}
